import { Box, Icon, Typography, styled } from "@tm/components"
import { SuggestionTextField } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { TitleType } from "@tm/models"

import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { ZodEffects, ZodObject, ZodRawShape } from "zod"
import { useEffect } from "react"
import FieldCol from "./FieldCol"

export type GeneralCustomerFields = {
    titleType: TitleType
    firstName: string
    lastName: string
    companyName: string
    street: string
    streetNo: string
    zip: string
    city: string
    country: string
    email: string
    phone: string
}

type Props = {
    fields: Partial<GeneralCustomerFields>
    schema: ZodObject<ZodRawShape> | ZodEffects<ZodObject<ZodRawShape>>
    onFormValid(isValid: boolean): void
    onUpdateFieldValue(path: keyof GeneralCustomerFields, value: string | undefined | TitleType | null): void
}
export default function GeneralCustomerForm(props: Props) {
    const { fields, onFormValid, schema, onUpdateFieldValue } = props
    const { translateText } = useLocalization()

    const titleItems: Record<string, string> = {
        1: translateText(145),
        2: translateText(146),
        3: translateText(108),
    }

    const {
        control,
        trigger,
        reset,
        formState: { isValid },
    } = useForm<GeneralCustomerFields>({
        resolver: zodResolver(schema),
        defaultValues: fields,
        mode: "onChange",
        reValidateMode: "onBlur",
    })

    useEffect(() => {
        reset(fields)
        trigger()
    }, [fields])

    useEffect(() => {
        onFormValid(isValid)
    }, [isValid])

    const handleUpdateCustomerTitle = (field: ControllerRenderProps<GeneralCustomerFields, "titleType">, value: string) => {
        const key = Object.keys(titleItems).find((k) => titleItems[k] === value)
        const enumValue = key ? (Number(key) as TitleType) : undefined

        field.onChange(enumValue)
        onUpdateFieldValue("titleType", enumValue)
    }

    const renderNameFields = () => {
        if (fields.titleType === TitleType.Company) {
            return <FieldCol control={control} label={1757} fieldName="companyName" onUpdateFieldValue={onUpdateFieldValue} />
        }

        return (
            <>
                <FieldCol control={control} label={119} fieldName="firstName" onUpdateFieldValue={onUpdateFieldValue} />
                <FieldCol control={control} label={104} fieldName="lastName" onUpdateFieldValue={onUpdateFieldValue} />
            </>
        )
    }

    const renderTitleType = () => {
        if (!("titleType" in fields)) {
            return
        }

        return (
            <Col sx={{ width: "6em" }}>
                <Controller
                    name="titleType"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SuggestionTextField
                            floatingLabel
                            label={translateText(118)}
                            value={field.value ? titleItems[field.value] : ""}
                            getSuggestionData={() => Object.values(titleItems)}
                            onChangeConfirm={(value) => {
                                handleUpdateCustomerTitle(field, value)
                            }}
                            path={[field.name]}
                            fieldState={fieldState}
                        />
                    )}
                />
            </Col>
        )
    }

    return (
        <Box>
            <Title>
                <Icon name="customer" size="extralarge" />
                <Typography fontSize="large" marginLeft={(theme) => theme.spacing(0.5)}>
                    {translateText(107)}
                </Typography>
            </Title>

            <Fields>
                <FieldsArea>
                    {renderTitleType()}
                    {renderNameFields()}
                </FieldsArea>

                <FieldsArea>
                    {"street" in fields && <FieldCol control={control} label={12914} fieldName="street" onUpdateFieldValue={onUpdateFieldValue} />}
                    {"streetNo" in fields && (
                        <FieldCol control={control} width="6em" label={3152} fieldName="streetNo" onUpdateFieldValue={onUpdateFieldValue} />
                    )}
                    {"zip" in fields && (
                        <FieldCol control={control} width="7.5em" label={112} fieldName="zip" onUpdateFieldValue={onUpdateFieldValue} />
                    )}
                    {"city" in fields && <FieldCol control={control} label={113} fieldName="city" onUpdateFieldValue={onUpdateFieldValue} />}
                    {"country" in fields && <FieldCol control={control} label={114} fieldName="country" onUpdateFieldValue={onUpdateFieldValue} />}
                </FieldsArea>
                <FieldsArea>
                    {"email" in fields && <FieldCol control={control} label={109} fieldName="email" onUpdateFieldValue={onUpdateFieldValue} />}
                    {"phone" in fields && <FieldCol control={control} label={110} fieldName="phone" onUpdateFieldValue={onUpdateFieldValue} />}
                </FieldsArea>
            </Fields>
        </Box>
    )
}

const Title = styled(Box)(() => ({
    display: "flex",
    padding: ".5em .5em 0 0",
    alignItems: "center",
}))

const Fields = styled(Box)(() => ({
    display: "inline-flex",
    flex: 1,
    margin: "0.5em 0.5em 0.5em 0",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: ".5em",
}))

const FieldsArea = styled(Box)(() => ({
    display: "flex",
    flex: 1,
    flexWrap: "wrap",
    flexBasis: "100%",
}))

const Col = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.5, 1, 0.5, 0),
    width: "15em",
    ".suggest ": {
        height: "100%",
    },
}))
